import Add from "@mui/icons-material/Add";
import { Alert, Box, Button, Paper, Stack, Tooltip, Typography } from "@mui/material";
import {
  GetWasteShipmentQuery,
  Issue,
  IssueLevel,
  WasteShipmentStatus,
} from "generated-graphql/graphql";
import { useState } from "react";

import { WasteContainerDrawer } from "../Containers/WasteContainerDrawer";
import { ShipmentContentsGrid } from "./ShipmentContentsGrid";
import { formatShipmentDate } from "./shipmentDisplay";
import {
  FieldDiff,
  ManifestOnlyStreams,
  ShipmentLevelDiffs,
  UncharacterizedComparison,
} from "./StreamComparison";
import { DisplayLine } from "./useStagedShipmentLines";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;

/**
 * Verbatim from the Figma frame. It no longer mentions the stream grouping,
 * which the group rows now state for themselves — what the reader cannot see
 * from the table is that this is the vendor's view and not a legal document.
 */
const VENDOR_VIEW_NOTE =
  "This is how the vendor sees the waste data on the manifest request, which is not an official manifest.";

/**
 * What state these contents are in, ahead of the note about whose view it is.
 *
 * The canceled case is why this is a function: a canceled shipment's table read
 * exactly like a draft's minus the Add button, listing amounts with nothing
 * saying they never left. The rail says so, but the amounts are here.
 */
function contentsCaption(shipment: Shipment): string {
  if (shipment.status === WasteShipmentStatus.Canceled) {
    return `Canceled — nothing shipped. ${VENDOR_VIEW_NOTE}`;
  }
  if (shipment.shippedDate) {
    return `Shipped ${formatShipmentDate(shipment.shippedDate)}. ${VENDOR_VIEW_NOTE}`;
  }
  return VENDOR_VIEW_NOTE;
}

type ShipmentContentsCardProps = {
  shipment: Shipment;
  /** Saved lines plus anything staged; see useStagedShipmentLines. */
  lines: DisplayLine[];
  editable: boolean;
  /**
   * Why the contents cannot be edited, when the shipment would otherwise be
   * editable — an attached document freezes them. Explains a disabled control
   * instead of leaving it inert and unexplained.
   */
  contentLockReason?: string;
  /** Standing issues for the shipment; per-container ones carry a modelId. */
  issues: Issue[];
  /**
   * The attached document's comparison, keyed by waste stream, so each stream's
   * row can state its own verdict beside the amount it disagrees with.
   */
  diffsByStream?: Map<string, FieldDiff[]>;
  /** Streams the attached document has and the shipment does not. */
  manifestOnlyDiffs?: FieldDiff[];
  /** Diffs about the shipment rather than any one stream. */
  shipmentLevelDiffs?: FieldDiff[];
  /** Waste with no stream on either side; compared as one bucket. */
  uncharacterizedDiffs?: FieldDiff[];
  /** Which kind of paper the comparison rows above are naming. */
  attachedDocumentKind?: Shipment["reconciliation"]["attachedDocumentKind"];
  onAddContainers: () => void;
  onEditLine: (line: DisplayLine) => void;
  onRemoveLine: (line: DisplayLine) => void;
  onRestoreLine: (line: DisplayLine) => void;
};

/**
 * A shipment's contents: what it says about itself, then the containers.
 *
 * The card owns the framing — the shipment-wide problems and the comparisons
 * that have no stream to sit on — and hands the containers themselves to a
 * grid, which groups them under the waste stream the vendor sees them as.
 * Splitting it there keeps the things that are about the whole shipment out of
 * a table whose rows are each about one stream.
 */
export function ShipmentContentsCard({
  shipment,
  lines,
  editable,
  contentLockReason,
  issues,
  diffsByStream,
  manifestOnlyDiffs,
  shipmentLevelDiffs,
  uncharacterizedDiffs,
  attachedDocumentKind,
  onAddContainers,
  onEditLine,
  onRemoveLine,
  onRestoreLine,
}: ShipmentContentsCardProps) {
  const locked = Boolean(contentLockReason);
  const shipped = !!shipment.shippedDate;
  // Which container's drawer is open over this page. Local state rather than
  // the URL: the shipment is what this route is about, and a container opened
  // from it is a look, not a place to land or link to.
  const [openContainerId, setOpenContainerId] = useState<string | null>(null);

  // Anything not pinned to a container belongs to the shipment as a whole.
  const shipmentIssues = issues.filter((issue) => !issue.modelId);

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mb: 0.5 }}
        spacing={2}
      >
        {/* The same overline every pane on this page wears, so the four read as
            one set rather than as a heading among labels. No tallies beside it:
            the grid states all three for itself now — a count per stream, a
            group row per stream, and a total on each. */}
        <Typography
          component="p"
          variant="overline"
          color="text.secondary"
          sx={{ m: 0, letterSpacing: "1px" }}
        >
          Shipment contents
        </Typography>
        {/* Disabled rather than hidden when a manifest holds the contents: the
            control is still the right one, it just needs the manifest detached
            first, and hiding it would leave no trace of why. */}
        {editable && (
          <Tooltip title={contentLockReason ?? ""}>
            <span>
              <Button size="small" startIcon={<Add />} onClick={onAddContainers} disabled={locked}>
                Add containers
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        {contentsCaption(shipment)}
      </Typography>

      {shipmentIssues.map((issue, i) => (
        <Alert
          key={`${issue.key}-${i}`}
          severity={issue.level === IssueLevel.Critical ? "error" : "warning"}
          sx={{ mb: 2 }}
        >
          {issue.message}
        </Alert>
      ))}

      {shipmentLevelDiffs && <ShipmentLevelDiffs diffs={shipmentLevelDiffs} />}
      {uncharacterizedDiffs && (
        <UncharacterizedComparison
          diffs={uncharacterizedDiffs}
          documentKind={attachedDocumentKind}
        />
      )}

      {lines.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center", backgroundColor: "grey.50", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No containers on this shipment yet.
          </Typography>
        </Box>
      ) : (
        <ShipmentContentsGrid
          lines={lines}
          editable={editable}
          contentLockReason={contentLockReason}
          shipped={shipped}
          issues={issues}
          // Undefined until a document is attached, which is what removes the
          // column rather than leaving it permanently blank. Keyed off
          // `attachedDocumentKind`, the resolver's own answer to which, if
          // either, is attached.
          diffsByStream={attachedDocumentKind != null ? diffsByStream : undefined}
          documentKind={attachedDocumentKind}
          onEditLine={onEditLine}
          onRemoveLine={onRemoveLine}
          onRestoreLine={onRestoreLine}
          onOpenContainer={setOpenContainerId}
        />
      )}

      {manifestOnlyDiffs && (
        <ManifestOnlyStreams diffs={manifestOnlyDiffs} documentKind={attachedDocumentKind} />
      )}

      <WasteContainerDrawer
        containerId={openContainerId ?? undefined}
        open={!!openContainerId}
        onClose={() => setOpenContainerId(null)}
      />
    </Paper>
  );
}
